import React from 'react';

import { Button, Group, Modal, Stack, TextInput, Title } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useShallowEffect } from '@mantine/hooks';
import { IconTags } from '@tabler/icons-react';
import useUpdateOtherWorkType from 'hooks/otherWorkType/useUpdateOtherWorkType';
import { OtherWorkType } from 'types';
import notificationHandler from 'utils/notifications';

interface IModalEditOtherWorkTypeProps {
  opened: boolean;
  handleClose: () => void;
  otherWorkType: OtherWorkType;
}

interface FormValues {
  name: string;
  otherWorkTypeId: string;
}

const ModalEditOtherWorkType = ({
  opened,
  handleClose,
  otherWorkType,
}: IModalEditOtherWorkTypeProps): JSX.Element => {
  const updateMutation = useUpdateOtherWorkType();

  const form = useForm<FormValues>({
    initialValues: {
      name: '',
      otherWorkTypeId: '',
    },

    validate: {
      name: value =>
        value.length < 3 || value.length > 120 ? 'Name must be between 3-120 characters' : null,
    },
  });

  const handleCloseAndReset = (): void => {
    handleClose();
    form.reset();
  };

  const { successNotification, errorNotification } = notificationHandler();
  const handleSubmit = (values: FormValues): void => {
    updateMutation.mutate(values, {
      onSuccess: () => {
        successNotification(
          'Updating other work type success',
          `${values.name} work type has been successfully updated.`,
        );
        handleCloseAndReset();
      },
      onError: error => {
        errorNotification('Update other work type failed', error.response?.data.message);
      },
    });
  };

  useShallowEffect(() => {
    form.setValues(otherWorkType);
  }, [otherWorkType]);

  return (
    <Modal
      centered
      opened={opened}
      onClose={handleCloseAndReset}
      title={<Title order={4}>Edit Other Work Type</Title>}
      size='auto'
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack>
          <TextInput
            required
            label='Name'
            {...form.getInputProps('name')}
            icon={<IconTags />}
            placeholder='Set Work Type Name'
            data-cy='editOtherWorkTypeTextInput'
          />
          <Group position='right'>
            <Button variant='subtle' onClick={handleCloseAndReset} disabled={updateMutation.isLoading}>
              Cancel
            </Button>
            <Button disabled={updateMutation.isLoading} type='submit' data-cy='saveEditOtherWorkTypeButton'>
              Save
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
};

export default ModalEditOtherWorkType;
